import { useState, useEffect } from "react";
import { Calendar, Clock, Send, Trash2, ChevronDown, ChevronRight, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface UnconvertedTest {
  id: string;
  email: string;
}

interface ScheduledEmail {
  id: string;
  subject: string;
  message: string;
  recipient_emails: string[];
  scheduled_at: string;
  status: string;
  sent_at: string | null;
  created_at: string;
}

interface ScheduledEmailsSectionProps {
  unconvertedTests: UnconvertedTest[];
  onRefresh: () => void;
}

const EMAIL_TEMPLATES = {
  urgence_24h: {
    label: "Urgence 24h",
    subject: "24h pour profiter de -20%",
    message: `Votre code : FLASH20

-20% sur tous nos abonnements pendant 24h.

12 mois à 48€ au lieu de 60€.

roidustreaming.fr/nos-offres`,
  },
  comparaison_prix: {
    label: "Comparaison prix",
    subject: "Vos abonnements streaming vous coûtent combien ?",
    message: `La plupart des gens paient entre 50 et 80 euros par mois pour leurs abonnements.

Chez nous, c'est 60 euros pour l'année.

Tout inclus : films, séries, sport en direct.

roidustreaming.fr/nos-offres`,
  },
  derniere_chance: {
    label: "Dernière chance",
    subject: "Dernier message + code -15%",
    message: `On ne vous relancera plus.

Si vous voulez revenir : code RETOUR15 pour -15%.

Valable 48h.

roidustreaming.fr/nos-offres`,
  },
  evenement: {
    label: "Événement",
    subject: "[À personnaliser] Ce week-end en direct",
    message: `[Personnalisez avec les événements du moment]

Tous les matchs en direct, qualité HD.

5€/mois.

roidustreaming.fr/nos-offres`,
  },
  custom: {
    label: "Email personnalisé",
    subject: "",
    message: "",
  },
};

const ScheduledEmailsSection = ({ unconvertedTests, onRefresh }: ScheduledEmailsSectionProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scheduledEmails, setScheduledEmails] = useState<ScheduledEmail[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Form states
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [emailTemplate, setEmailTemplate] = useState<string>("urgence_24h");
  const [customSubject, setCustomSubject] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [scheduling, setScheduling] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchScheduledEmails();
    }
  }, [isOpen]);

  const fetchScheduledEmails = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("scheduled_emails")
        .select("*")
        .order("scheduled_at", { ascending: true });

      if (error) throw error;
      setScheduledEmails(data || []);
    } catch (error) {
      console.error("Error fetching scheduled emails:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleEmailSelection = (email: string) => {
    setSelectedEmails((prev) =>
      prev.includes(email)
        ? prev.filter((e) => e !== email)
        : [...prev, email]
    );
  };

  const selectAllUnconverted = () => {
    setSelectedEmails(unconvertedTests.map((t) => t.email));
  };

  const clearSelection = () => {
    setSelectedEmails([]);
  };

  const getCurrentEmailContent = () => {
    if (emailTemplate === "custom") {
      return { subject: customSubject, message: customMessage };
    }
    const template = EMAIL_TEMPLATES[emailTemplate as keyof typeof EMAIL_TEMPLATES];
    return { subject: template.subject, message: template.message };
  };

  const handleScheduleEmail = async () => {
    if (selectedEmails.length === 0) {
      toast.error("Sélectionnez au moins un email");
      return;
    }

    if (!scheduledDate || !scheduledTime) {
      toast.error("Veuillez sélectionner une date et une heure");
      return;
    }

    const { subject, message } = getCurrentEmailContent();
    if (!subject.trim() || !message.trim()) {
      toast.error("Veuillez remplir le sujet et le message");
      return;
    }

    // Convertir la date/heure française en UTC
    const parisDateTime = `${scheduledDate}T${scheduledTime}:00`;
    const scheduledAt = new Date(parisDateTime + "+01:00").toISOString(); // Paris est UTC+1 en hiver

    setScheduling(true);
    try {
      const { error } = await supabase.from("scheduled_emails").insert({
        subject,
        message,
        recipient_emails: selectedEmails,
        scheduled_at: scheduledAt,
        status: "pending",
      });

      if (error) throw error;

      toast.success(`Email programmé pour ${scheduledDate} à ${scheduledTime} (heure française)`);
      clearSelection();
      setScheduledDate("");
      setScheduledTime("");
      fetchScheduledEmails();
      onRefresh();
    } catch (error) {
      console.error("Error scheduling email:", error);
      toast.error("Erreur lors de la programmation");
    } finally {
      setScheduling(false);
    }
  };

  const handleDeleteScheduled = async (id: string) => {
    try {
      const { error } = await supabase
        .from("scheduled_emails")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast.success("Email programmé supprimé");
      fetchScheduledEmails();
    } catch (error) {
      console.error("Error deleting scheduled email:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const formatDateTimeFrench = (isoDate: string) => {
    return new Date(isoDate).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Paris",
    });
  };

  const pendingEmails = scheduledEmails.filter((e) => e.status === "pending");
  const sentEmails = scheduledEmails.filter((e) => e.status === "sent");

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          className="w-full justify-between text-foreground hover:bg-muted"
        >
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-blue-500" />
            <span>Emails programmés</span>
          </div>
          <div className="flex items-center gap-2">
            {pendingEmails.length > 0 && (
              <Badge variant="secondary" className="text-xs bg-blue-500/20 text-blue-400">
                {pendingEmails.length} en attente
              </Badge>
            )}
            {isOpen ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </div>
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2">
        <div className="space-y-4 px-2">
          {/* Emails programmés en attente */}
          {pendingEmails.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">En attente d'envoi</Label>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {pendingEmails.map((email) => (
                  <div
                    key={email.id}
                    className="bg-muted/50 rounded-md p-3 flex items-start justify-between gap-2"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {email.subject}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {formatDateTimeFrench(email.scheduled_at)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        <Users className="w-3 h-3 inline mr-1" />
                        {email.recipient_emails.length} destinataire(s)
                      </p>
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      onClick={() => handleDeleteScheduled(email.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Programmer un nouvel email */}
          <div className="border-t border-border pt-4 space-y-3">
            <Label className="text-xs text-muted-foreground font-medium">
              Programmer un email de masse
            </Label>

            {/* Selection buttons */}
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={selectAllUnconverted}
                className="flex-1 text-xs border-border"
              >
                <Users className="w-3 h-3 mr-1" />
                Tout ({unconvertedTests.length})
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={clearSelection}
                className="flex-1 text-xs border-border"
                disabled={selectedEmails.length === 0}
              >
                Annuler
              </Button>
            </div>

            {/* Email list */}
            <div className="space-y-1 max-h-24 overflow-y-auto">
              {unconvertedTests.length === 0 ? (
                <p className="text-muted-foreground text-sm py-2">
                  Aucun test non converti
                </p>
              ) : (
                unconvertedTests.map((test) => (
                  <label
                    key={test.id}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted transition-colors cursor-pointer"
                  >
                    <Checkbox
                      checked={selectedEmails.includes(test.email)}
                      onCheckedChange={() => toggleEmailSelection(test.email)}
                    />
                    <span className="text-sm text-foreground truncate flex-1">
                      {test.email}
                    </span>
                  </label>
                ))
              )}
            </div>

            {selectedEmails.length > 0 && (
              <div className="space-y-3 pt-2 border-t border-border">
                {/* Template selection */}
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Template</Label>
                  <Select value={emailTemplate} onValueChange={setEmailTemplate}>
                    <SelectTrigger className="w-full bg-input border-border text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(EMAIL_TEMPLATES).map(([key, value]) => (
                        <SelectItem key={key} value={key}>
                          {value.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {emailTemplate === "custom" ? (
                  <>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Sujet</Label>
                      <Input
                        type="text"
                        value={customSubject}
                        onChange={(e) => setCustomSubject(e.target.value)}
                        placeholder="Sujet de l'email"
                        className="bg-input border-border text-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Message</Label>
                      <Textarea
                        value={customMessage}
                        onChange={(e) => setCustomMessage(e.target.value)}
                        placeholder="Votre message..."
                        className="min-h-[60px] text-sm bg-input border-border"
                      />
                    </div>
                  </>
                ) : (
                  <div className="bg-muted/50 rounded-md p-3 space-y-1">
                    <p className="text-xs text-muted-foreground">Aperçu :</p>
                    <p className="text-sm font-medium text-foreground">
                      {EMAIL_TEMPLATES[emailTemplate as keyof typeof EMAIL_TEMPLATES]?.subject}
                    </p>
                  </div>
                )}

                {/* Date/Time selection */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Date</Label>
                    <Input
                      type="date"
                      value={scheduledDate}
                      onChange={(e) => setScheduledDate(e.target.value)}
                      className="bg-input border-border text-sm"
                      min={new Date().toISOString().split("T")[0]}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Heure (Paris)</Label>
                    <Input
                      type="time"
                      value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                      className="bg-input border-border text-sm"
                    />
                  </div>
                </div>

                <Button
                  size="sm"
                  onClick={handleScheduleEmail}
                  disabled={scheduling || !scheduledDate || !scheduledTime}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  {scheduling
                    ? "Programmation..."
                    : `Programmer pour ${selectedEmails.length} personne(s)`}
                </Button>
              </div>
            )}
          </div>

          {/* Historique des emails envoyés */}
          {sentEmails.length > 0 && (
            <div className="border-t border-border pt-4 space-y-2">
              <Label className="text-xs text-muted-foreground">Historique (envoyés)</Label>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {sentEmails.slice(0, 5).map((email) => (
                  <div
                    key={email.id}
                    className="bg-green-500/10 rounded-md p-2"
                  >
                    <p className="text-sm text-foreground truncate">{email.subject}</p>
                    <p className="text-xs text-green-400">
                      Envoyé le {formatDateTimeFrench(email.sent_at!)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default ScheduledEmailsSection;
